import React, { useState, useCallback, useRef, useEffect } from 'react';
import { getSite } from '../../services/siteManagement/siteManagement.service';
import type { SiteDetail, SiteTreeNode } from '../../services/siteManagement/siteManagement.types';
import { useNavigate } from 'react-router-dom';

interface SidebarTreeProps {
  nodes?: SiteTreeNode[] | null;
  onSelect?: (node: SiteTreeNode) => void;
  collapsed?: boolean;
  allowedSiteIdentifiers: string[] | undefined;
  allowedTopLevelKeys: string[] | null | undefined;
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined;
  unacknowledgedCounts: Record<string, number> | undefined;
  selectedId?: string | null;
  setSelectedId?: (id: string | null) => void;
  expandedKeys?: Set<string>;
  setExpandedKeys?: React.Dispatch<React.SetStateAction<Set<string>>>;
  startLevel?: number;
}

// Optional runtime fields not declared in the base SiteTreeNode type
interface ExtendedNode extends SiteTreeNode {
  siteGuid?: string | null;
  childrenCount?: number | null;
}

type NodeFieldValue = string | number | null;

const PARENT_BUTTON_CLASS = 'sidebar-tree__button--parent';

function isCustomerKey(key: string): boolean {
  return key.startsWith('customer-');
}

function isVtKey(key: string): boolean {
  return key.startsWith('vt-') || key.includes('-vt-');
}

function isSiteKey(key: string): boolean {
  return key.startsWith('site-') || key.includes('-site-');
}

function isNodeCustomer(node: SiteTreeNode): boolean {
  return node.type === 'customer' || isCustomerKey(node.key);
}

function isNodeVt(node: SiteTreeNode): boolean {
  return node.type === 'vt' || isVtKey(node.key);
}

function isNodeSite(node: SiteTreeNode): boolean {
  return node.type === 'site' || isSiteKey(node.key);
}

function parseNumericIdFromKey(key: string): number {
  const parts = key.split('-').filter(p => p !== '');
  let candidate: string | undefined;
  if (parts.length > 0) {
    candidate = parts[parts.length - 1];
  }
  if (!candidate || Number.isNaN(Number(candidate))) {
    candidate = parts[1];
  }
  if (candidate !== undefined) {
    return Number(candidate);
  }
  return NaN;
}

function navigateCustomer(key: string, navigate: (path: string) => void): void {
  const numId = parseNumericIdFromKey(key);
  if (!Number.isNaN(numId)) {
    navigate(`/customers/${encodeURIComponent(String(numId))}`);
  } else {
    navigate(`/customers/${encodeURIComponent(key)}`);
  }
}

function navigateVt(key: string, navigate: (path: string) => void): void {
  const numId = parseNumericIdFromKey(key);
  if (!Number.isNaN(numId)) {
    navigate(`/virtualTenants/vt/${encodeURIComponent(String(numId))}`);
  } else {
    navigate(`/virtualTenants/vt/${encodeURIComponent(key)}`);
  }
}

function navigateForNode(node: SiteTreeNode, navigate: (path: string) => void): void {
  if (isNodeCustomer(node)) {
    navigateCustomer(node.key, navigate);
    return;
  }
  if (isNodeVt(node)) {
    navigateVt(node.key, navigate);
    return;
  }
  navigate(`/sites/${encodeURIComponent(String(node.sirId ?? node.key))}`);
}

function mapSiteDetailChild(c: SiteDetail): SiteTreeNode {
  return {
    key: `site-${c.id}`,
    title: c.name || String(c.id),
    type: 'site',
    siteIdentifier: c.siteIdentifier ?? null,
    sirId: c.id ?? null,
    shId: c.parentId ?? null,
  };
}

async function lazyLoadCustomerChildren(
  cid: number,
  setLoadedChildren: (children: SiteTreeNode[]) => void,
  toggleExpanded: (key: string) => void,
  setFocusedId: (id: string | null) => void,
  nodeId: string
): Promise<void> {
  try {
    const res = await getSite(cid);
    if (res.success && res.data && Array.isArray(res.data.children)) {
      const mapped = res.data.children.map(mapSiteDetailChild);
      setLoadedChildren(mapped);
      toggleExpanded(nodeId);
      setFocusedId(nodeId);
    }
  } catch (e) {
    console.info('Failed to load children for customer', cid, e);
  }
}

function tryLazyLoadChildren(
  isCustomerNode: boolean,
  nodeKey: string,
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined,
  setLoadedChildren: (children: SiteTreeNode[]) => void,
  toggleExpanded: (key: string) => void,
  setFocusedId: (id: string | null) => void,
  nodeId: string
): void {
  if (!isCustomerNode) { return; }
  const cid = Number(nodeKey.split('-')[1]);
  if (Number.isNaN(cid)) { return; }
  const pre = allowedChildrenMap?.[nodeKey];
  if (pre && pre.length > 0) {
    setLoadedChildren(pre);
    toggleExpanded(nodeId);
    setFocusedId(nodeId);
    return;
  }
  void lazyLoadCustomerChildren(cid, setLoadedChildren, toggleExpanded, setFocusedId, nodeId);
}

function valToLower(v?: NodeFieldValue): string {
  if (v === undefined || v === null) { return ''; }
  return String(v).trim().toLowerCase();
}

function isInAllowed(v: NodeFieldValue, set: Set<string>): boolean {
  const lower = valToLower(v);
  return lower !== '' && set.has(lower);
}

function nodeMatchesIdentifier(n: ExtendedNode, normalizedAllowed: Set<string>): boolean {
  const fields: Array<NodeFieldValue> = [
    n.siteIdentifier ?? null,
    n.siteGuid ?? null,
    n.sirId ?? null,
    n.shId ?? null,
    n.key,
  ];
  return fields.some(v => isInAllowed(v, normalizedAllowed));
}

function matchesAllowed(
  n: SiteTreeNode,
  allowedSiteIdentifiers: string[] | undefined,
  allowedTopLevelKeys: string[] | null | undefined
): boolean {
  if (!allowedSiteIdentifiers || allowedSiteIdentifiers.length === 0) { return true; }
  if (allowedTopLevelKeys && allowedTopLevelKeys.length > 0 && allowedTopLevelKeys.includes(n.key)) {
    return true;
  }
  const normalizedAllowed = new Set(allowedSiteIdentifiers.map(s => String(s).trim().toLowerCase()));
  return nodeMatchesIdentifier(n as ExtendedNode, normalizedAllowed);
}

function filterRecursive(
  items: SiteTreeNode[],
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined,
  allowedSiteIdentifiers: string[] | undefined,
  allowedTopLevelKeys: string[] | null | undefined
): SiteTreeNode[] {
  const out: SiteTreeNode[] = [];
  for (const it of items) {
    let childrenToUse = it.children;
    if (allowedChildrenMap?.[it.key]) {
      childrenToUse = allowedChildrenMap[it.key];
    }
    let children: SiteTreeNode[] = [];
    if (childrenToUse) {
      children = filterRecursive(childrenToUse, allowedChildrenMap, allowedSiteIdentifiers, allowedTopLevelKeys);
    }
    if (matchesAllowed(it, allowedSiteIdentifiers, allowedTopLevelKeys) || children.length > 0) {
      const nodeToPush: SiteTreeNode = { ...it };
      if (children.length > 0) {
        nodeToPush.children = children;
      } else {
        delete nodeToPush.children;
      }
      out.push(nodeToPush);
    }
  }
  return out;
}

function computeButtonClass(
  node: SiteTreeNode,
  effectiveChildren: SiteTreeNode[] | undefined,
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined,
  allowedTopLevelKeys: string[] | null | undefined
): string {
  if (effectiveChildren && effectiveChildren.length > 0) { return PARENT_BUTTON_CLASS; }
  const extNode = node as ExtendedNode;
  if ((extNode.childrenCount ?? node.siteCount ?? 0) > 0) { return PARENT_BUTTON_CLASS; }
  if (allowedChildrenMap?.[node.key]?.length) { return PARENT_BUTTON_CLASS; }
  if (allowedTopLevelKeys?.includes(node.key)) { return PARENT_BUTTON_CLASS; }
  return '';
}

function computeTooltipStyle(
  position: { top: number; left?: number; right?: number }
): React.CSSProperties {
  const style: React.CSSProperties = {
    position: 'fixed',
    top: `${position.top}px`,
    transform: 'translateY(-50%)',
    zIndex: 9999,
    pointerEvents: 'none',
  };
  if (position.left !== undefined) {
    style.left = `${position.left}px`;
  }
  if (position.right !== undefined) {
    style.right = `${position.right}px`;
  }
  return style;
}

function shouldApplyFilter(
  allowedSiteIdentifiers: string[] | undefined,
  allowedTopLevelKeys: string[] | null | undefined
): boolean {
  return Boolean(allowedSiteIdentifiers && allowedSiteIdentifiers.length > 0) && Array.isArray(allowedTopLevelKeys);
}


const TreeNode: React.FC<{
  node: SiteTreeNode;
  level: number;
  parentPath?: string;
  onSelect?: (n: SiteTreeNode) => void;
  registerFocusable: (ref: HTMLButtonElement | null, id: string) => void;
  focusedId?: string | null;
  setFocusedId: (id: string | null) => void;
  selectedId?: string | null;
  setSelectedId?: (id: string | null) => void;
  collapsed?: boolean;
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined;
  allowedTopLevelKeys: string[] | null | undefined;
  unacknowledgedCounts: Record<string, number> | undefined;
  expandedKeys: Set<string>;
  toggleExpanded: (key: string) => void;
}> = ({ node, level, onSelect, parentPath = '', registerFocusable, focusedId, setFocusedId, selectedId, setSelectedId, collapsed, allowedChildrenMap, allowedTopLevelKeys, unacknowledgedCounts, expandedKeys, toggleExpanded }) => {
  // allowedTopLevelKeys is intentionally not destructured above to keep the function signature compact; use arguments list
  const open = expandedKeys.has(node.key);
  const [loadedChildren, setLoadedChildren] = useState<SiteTreeNode[] | undefined>(undefined);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0, left: 0 });
  const navigate = useNavigate();
  const id = node.key;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const effectiveChildren = node.children ?? loadedChildren;

  useEffect(() => {
    registerFocusable(btnRef.current, id);
    return () => registerFocusable(null, id);
  }, [id, registerFocusable]);

  const handleActivate = useCallback(() => {
    const isCustomerNode = isNodeCustomer(node);
    const isSiteNode = isNodeSite(node);
    const hasChildren = Boolean(effectiveChildren && effectiveChildren.length > 0);
    if (hasChildren && !isCustomerNode && !isSiteNode) {
      setSelectedId?.(node.key);
      toggleExpanded(node.key);
      setFocusedId(id);
      return;
    }
    onSelect?.(node);
    setSelectedId?.(node.key);
    navigateForNode(node, navigate);
    if ((isCustomerNode || isSiteNode) && hasChildren) {
      toggleExpanded(node.key);
      setFocusedId(id);
      return;
    }
    tryLazyLoadChildren(isCustomerNode, node.key, allowedChildrenMap, setLoadedChildren, toggleExpanded, setFocusedId, id);
  }, [node, navigate, onSelect, id, setFocusedId, setSelectedId, effectiveChildren, allowedChildrenMap, toggleExpanded]);

  const handleMouseEnter = useCallback(() => {
    if (collapsed && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const isRTL = document.body.classList.contains('right-left-layout');
      
      if (isRTL) {
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 8
        });
      } else {
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 8
        });
      }
      setShowTooltip(true);
    }
  }, [collapsed]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
    // Left/Right handled by tree container (roving focus)
  }, [handleActivate]);

  const unackCount = unacknowledgedCounts?.[node.key] ?? 0;

  const isExpandable = Boolean(effectiveChildren && effectiveChildren.length > 0);
  let ariaExpanded: boolean | undefined;
  if (isExpandable) {
    ariaExpanded = open;
  }

  let computedTabIndex = -1;
  if (focusedId === id) {
    computedTabIndex = 0;
  }

  const tooltipStyle = computeTooltipStyle(tooltipPosition);
  const buttonClass = computeButtonClass(node, effectiveChildren, allowedChildrenMap, allowedTopLevelKeys);

  return (
    <li className={`sidebar-tree__item sidebar-tree__item--level-${level}`} role="none">
      <button
        ref={btnRef}
        role="treeitem"
        aria-expanded={ariaExpanded}
        aria-selected={selectedId === id}
        tabIndex={computedTabIndex}
        aria-label={node.title}
        className={`sidebar-tree__button ${buttonClass}`}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        data-node-id={id}
      >
        <span className="sidebar-tree__title">{node.title}</span>
        {unackCount > 0 && (
          <span className="sidebar-tree__badge">{unackCount}</span>
        )}
      </button>

      {collapsed && showTooltip && (
        <div
          className="sidebar-tree__tooltip"
          style={tooltipStyle}
        >
          {node.title}
        </div>
      )}

      <TreeNodeChildren
        open={open}
        effectiveChildren={effectiveChildren}
        level={level}
        id={id}
        parentPath={parentPath}
        onSelect={onSelect}
        registerFocusable={registerFocusable}
        focusedId={focusedId}
        setFocusedId={setFocusedId}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        collapsed={collapsed}
        allowedChildrenMap={allowedChildrenMap}
        allowedTopLevelKeys={allowedTopLevelKeys}
        unacknowledgedCounts={unacknowledgedCounts}
        expandedKeys={expandedKeys}
        toggleExpanded={toggleExpanded}
      />
    </li>
  );
};


interface TreeNodeChildrenProps {
  open: boolean;
  effectiveChildren: SiteTreeNode[] | undefined;
  level: number;
  id: string;
  parentPath: string;
  onSelect?: (n: SiteTreeNode) => void;
  registerFocusable: (ref: HTMLButtonElement | null, id: string) => void;
  focusedId?: string | null;
  setFocusedId: (id: string | null) => void;
  selectedId?: string | null;
  setSelectedId?: (id: string | null) => void;
  collapsed?: boolean;
  allowedChildrenMap: Record<string, SiteTreeNode[]> | undefined;
  allowedTopLevelKeys: string[] | null | undefined;
  unacknowledgedCounts: Record<string, number> | undefined;
  expandedKeys: Set<string>;
  toggleExpanded: (key: string) => void;
}

const TreeNodeChildren: React.FC<TreeNodeChildrenProps> = ({
  open, effectiveChildren, level, id, parentPath, onSelect, registerFocusable,
  focusedId, setFocusedId, selectedId, setSelectedId, collapsed, allowedChildrenMap,
  allowedTopLevelKeys, unacknowledgedCounts, expandedKeys, toggleExpanded,
}) => {
  if (!open || !effectiveChildren?.length) { return null; }
  return (
    <ul className="sidebar-tree__group">
      {effectiveChildren.map(c => (
        <TreeNode
          key={c.key}
          node={c}
          level={level + 1}
          onSelect={onSelect}
          parentPath={`${parentPath}/${id}`}
          registerFocusable={registerFocusable}
          focusedId={focusedId}
          setFocusedId={setFocusedId}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          collapsed={collapsed}
          allowedChildrenMap={allowedChildrenMap}
          allowedTopLevelKeys={allowedTopLevelKeys}
          unacknowledgedCounts={unacknowledgedCounts}
          expandedKeys={expandedKeys}
          toggleExpanded={toggleExpanded}
        />
      ))}
    </ul>
  );
};

const SidebarTree: React.FC<SidebarTreeProps> = ({ nodes = [], onSelect, collapsed = false, allowedSiteIdentifiers, allowedTopLevelKeys, allowedChildrenMap, selectedId: selectedIdProp, setSelectedId: setSelectedIdProp, unacknowledgedCounts, expandedKeys: expandedKeysProp, setExpandedKeys: setExpandedKeysProp, startLevel = 0 }) => {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // selection is explicit (activated), start as null so nothing is selected on load
  const [selectedIdLocal, setSelectedIdLocal] = useState<string | null>(null);
  // track which nodes are expanded - persists across data refreshes
  const [expandedKeysLocal, setExpandedKeysLocal] = useState<Set<string>>(new Set());
  
  // Use prop if provided, otherwise use local state
  const expandedKeys = expandedKeysProp ?? expandedKeysLocal;
  const setExpandedKeys = setExpandedKeysProp ?? setExpandedKeysLocal;
  let selectedId = selectedIdLocal;
  if (selectedIdProp !== undefined) {
    selectedId = selectedIdProp;
  }
  const setSelectedId: (id: string | null) => void = setSelectedIdProp ?? setSelectedIdLocal;
  const focusableMap = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  useEffect(() => {
    // set initial focus to first node for keyboard navigation, but do NOT mark it selected
    if (!focusedId && nodes && nodes.length > 0) {
      setFocusedId(nodes[0].key);
    }
  }, [nodes, focusedId]);

  const toggleExpanded = useCallback((key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const registerFocusable = useCallback((ref: HTMLButtonElement | null, id: string) => {
    if (ref) {
      focusableMap.current.set(id, ref);
    } else {
      focusableMap.current.delete(id);
    }
  }, []);

  const moveFocus = (direction: 'next' | 'prev') => {
    const keys = Array.from(focusableMap.current.keys());
    const idx = keys.indexOf(focusedId || '');
    if (idx === -1) { return; }
    let nextIdx: number;
    if (direction === 'next') {
      nextIdx = Math.min(keys.length - 1, idx + 1);
    } else {
      nextIdx = Math.max(0, idx - 1);
    }
    const nextId = keys[nextIdx];
    setFocusedId(nextId);
    const el = focusableMap.current.get(nextId);
    el?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveFocus('next');
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocus('prev');
        break;
      default:
        break;
    }
  };

  // pass a non-array accidentally which causes runtime n.map errors.
  if (!nodes) {return null;}
  let nodeListRaw: SiteTreeNode[] = [];
  if (Array.isArray(nodes)) {
    nodeListRaw = nodes;
  } else if (typeof nodes === 'object') {
    nodeListRaw = Object.values(nodes as Record<string, SiteTreeNode>);
  } else {
    nodeListRaw = [];
  }

  // Only apply filtering when we have a real array of allowed top-level keys.
  const shouldFilter = shouldApplyFilter(allowedSiteIdentifiers, allowedTopLevelKeys);
  let nodeList = nodeListRaw;
  if (shouldFilter) {
    nodeList = filterRecursive(nodeListRaw, allowedChildrenMap, allowedSiteIdentifiers, allowedTopLevelKeys);
  }

  if (nodeList.length === 0) {return null;}

  return (
    <div className="sidebar-tree" role="tree" aria-label="Sites" onKeyDown={handleKeyDown} tabIndex={0}>
      <ul className="sidebar-tree__list">
        {nodeList.map(n => (
          <TreeNode
            key={n.key}
            node={n}
            level={startLevel}
            onSelect={onSelect}
            registerFocusable={registerFocusable}
            focusedId={focusedId}
            setFocusedId={setFocusedId}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            collapsed={collapsed}
            allowedChildrenMap={allowedChildrenMap}
            allowedTopLevelKeys={allowedTopLevelKeys}
            unacknowledgedCounts={unacknowledgedCounts}
            expandedKeys={expandedKeys}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </ul>
    </div>
  );
};

export default SidebarTree;
